import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, RotateCcw, ShieldAlert } from 'lucide-react';
import { format } from 'date-fns';

function useReissuePermission({ activity, issue, officers }) {
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });
  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find(p => p.user_email === user?.email);
    },
    enabled: !!user
  });
  const { data: objectives = [] } = useQuery({ queryKey: ['objectives'], queryFn: () => base44.entities.Objective.list() });
  const { data: missions = [] } = useQuery({ queryKey: ['missions'], queryFn: () => base44.entities.Mission.list() });

  if (!user || !activity) return false;

  const email = user.email;
  const isAdmin = user.role === 'admin';
  const isCommander = isAdmin || profile?.custom_role === 'Commander' || profile?.custom_role === 'Commander in Chief';

  // (a) Activity owner (Responsible/Accountable)
  if (activity.responsible_email === email || activity.accountable_email === email) return true;

  // (b) Original issue submitter — check the progress report reporter
  const report = issue?._report;
  if (report?.reported_by === email) return true;

  // (c) Assigned track owner on the linked Issue entity
  if (issue?.linked_issue_id) {
    // We don't have the full Issue entity here, but track owners are checked on the Issues page
  }

  // (d) Higher authority — objective/mission owners or commanders
  if (isCommander) return true;
  const objective = objectives.find(o => o.id === activity.objective_id);
  if (objective?.owner_email === email) return true;
  const mission = objective ? missions.find(m => m.id === objective.mission_id) : null;
  if (mission?.owner_email === email || mission?.sponsor_email === email || mission?.assigned_to_email === email) return true;

  return false;
}

export default function RemovedIssuesSection({ activity, reportIssues, progressReports, officers }) {
  const queryClient = useQueryClient();

  const reissueReportIssueMutation = useMutation({
    mutationFn: async (id) => {
      const user = await base44.auth.me();
      await base44.entities.ReportIssue.update(id, {
        removed_from_list: null,
        reissued_by: user.email,
        reissued_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    }
  });

  // Find removed issues for this activity
  const activityReports = progressReports.filter(r => r.activity_id === activity.id);
  const removedIssues = reportIssues.filter(ri => {
    const belongsToActivity = activityReports.some(r => r.id === ri.progress_report_id);
    return belongsToActivity && ri.removed_from_list;
  }).map(ri => ({
    ...ri,
    _report: progressReports.find(r => r.id === ri.progress_report_id)
  }));

  if (removedIssues.length === 0) return null;

  return (
    <Card className="glass-panel border-amber-500/30 bg-amber-500/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-amber-400 mb-3">
          <AlertTriangle className="w-4 h-4" />
          <h4 className="text-sm font-semibold">Removed Issues</h4>
        </div>
        {removedIssues.map((issue) => (
          <RemovedIssueRow
            key={issue.id}
            issue={issue}
            activity={activity}
            officers={officers}
            onReissue={() => reissueReportIssueMutation.mutate(issue.id)}
            isReissuing={reissueReportIssueMutation.isPending}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function RemovedIssueRow({ issue, activity, officers, onReissue, isReissuing }) {
  const canReissue = useReissuePermission({ activity, issue, officers });
  const remover = officers.find(o => o.user_email === issue.removed_from_list?.removed_by);

  return (
    <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm text-slate-300 mb-2">{issue.issue_description}</p>
          <p className="text-xs text-slate-500">
            Removed from issue list by {remover?.callsign || issue.removed_from_list?.removed_by} on{' '}
            {format(new Date(issue.removed_from_list?.removed_at), 'MMM d, yyyy HH:mm')}
          </p>
        </div>
        {canReissue ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onReissue}
            disabled={isReissuing}
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <RotateCcw className="w-3 h-3 mr-2" />
            Reissue
          </Button>
        ) : (
          <div className="flex items-center gap-1 text-[10px] text-slate-600" title="Only activity owners, the original reporter, track owners, or higher authority can reissue">
            <ShieldAlert className="w-3 h-3" />
            <span>No permission</span>
          </div>
        )}
      </div>
    </div>
  );
}